import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, MapPin, Phone } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { BusinessHours } from "@/components/BusinessHours";
import { CITY_HUBS } from "@/data/cityHubs";
import { canonicalLink, canonicalUrl, geoMeta, SITE_URL } from "@/lib/seo";
import { businessHeadScripts } from "@/lib/businessJsonLd";

const TITLE = "Texas Service Area FAQs — Awnings, Shade & Covers | Texas Made Shade";
const DESCRIPTION =
  "Answers for Texas customers: where we install awnings and shade, travel and measure fees, lead times, permits and HOA drawings, shop hours and directions in Seguin, plus San Antonio and Austin coverage.";

/**
 * Local FAQ hub — the "how do we work with you, where, and how fast"
 * questions that come in by phone every week. Deliberately city-agnostic
 * (statewide logistics) so it complements, rather than duplicates, the
 * city-specific FAQs already on each city hub page.
 */
const FAQS: { q: string; a: string }[] = [
  {
    q: "What parts of Texas do you install in?",
    a: "Our shop is in Seguin, TX and our own crews install across Central Texas, the San Antonio and Austin metros, the Hill Country, South Texas and the Gulf Coast — including New Braunfels, San Marcos, Boerne, Kerrville, Fredericksburg, Corpus Christi, Rockport and Port Aransas. If you're outside that footprint, call us: we still take on projects statewide when the scope makes the trip worthwhile.",
  },
  {
    q: "Do you charge for the estimate or the on-site measurement?",
    a: "No. On-site measurements and estimates inside our regular service area are free, and we do not use commissioned salespeople. Send photos and rough dimensions first and we can usually give you a budget range on the same business day, then confirm it with a site visit.",
  },
  {
    q: "Do I have to come to Seguin, or do you come to me?",
    a: "We come to you for measuring and installation. You're also welcome at the shop at 959 East Kingsbury Street, Seguin, TX 78155 to see fabric and frame samples in person and meet the people building your project.",
  },
  {
    q: "What are your shop hours?",
    a: "Monday through Thursday 8:30am to 5:00pm and Friday 8:30am to 4:00pm, Central time. Phone and web quote requests are answered the same business day whenever possible — call +1 (830) 390-9588 or use any quote form on the site.",
  },
  {
    q: "How long does a project take from quote to install?",
    a: "It depends on the product and the season. Recovers and canvas work move fastest; welded frames, motorized systems and engineered structures take longer because they're patterned and built for your opening. Spring and early summer are our busiest months in San Antonio and Austin, so book ahead of the heat if you have a date in mind.",
  },
  {
    q: "Do you handle permits, engineering or HOA approval?",
    a: "We can provide drawings and specifications you can submit to your HOA or municipality, and we coordinate engineered stamps when a commercial or structural project requires them. Permit filing requirements vary by city, so we confirm what your jurisdiction needs before fabrication starts.",
  },
  {
    q: "Do you do commercial work for Texas businesses?",
    a: "Yes — storefront and restaurant awnings, walkway canopies, patio shade for bars and hotels, school and church shade structures, and welded fabrication. We pattern on site, build the frame in our Seguin shop, sew the canvas in-house and install with our own crew, which keeps one company accountable for the whole job.",
  },
  {
    q: "Do you service and repair what you install?",
    a: "Yes. Local service is the reason to buy locally: recovers, re-tensioning, frame repair, motor and control troubleshooting, seasonal take-down and maintenance memberships are all handled by the same shop that built the project.",
  },
  {
    q: "Is financing available?",
    a: "Yes — financing is available through Acorn Finance on outdoor living projects such as pergolas, motorized screens, retractable awnings, lighting and misting. Ask us for the details when we quote your project.",
  },
  {
    q: "Which fabrics hold up in Texas sun and Gulf humidity?",
    a: "We build primarily with Sunbrella® and Tempotest® solution-dyed acrylic for awnings, Phifertex® and mesh shade cloth for sail shades and screens, and Sunbrella® Marine for boat covers. All of them are UV-stable and mildew-resistant, which matters as much on the coast as it does inland.",
  },
];

export const Route = createFileRoute("/local-faqs")({
  head: () => ({
    links: [canonicalLink("/local-faqs")],
    meta: [
      ...geoMeta(),
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Texas Service Area FAQs — Texas Made Shade" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: canonicalUrl("/local-faqs") },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    scripts: [
      ...businessHeadScripts(),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${canonicalUrl("/local-faqs")}#faq`,
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Texas Service Area FAQs", item: canonicalUrl("/local-faqs") },
          ],
        }),
      },
    ],
  }),
  component: LocalFaqsPage,
});

function LocalFaqsPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main id="main-content" tabIndex={-1} className="bg-cream">
        <section className="border-b-8 border-navy-deep bg-navy-deep py-14 text-white lg:py-20">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <p className="font-display text-xs uppercase tracking-[0.3em] text-red">
              Local Answers • Real Texas Shop
            </p>
            <h1 className="mt-3 font-display text-4xl font-bold uppercase leading-tight md:text-5xl">
              Texas Service Area FAQs
            </h1>
            <p className="mt-4 max-w-3xl text-base text-white/85 md:text-lg">
              Where we install, what a measure costs, how long projects take, and how to reach the
              shop. Based in Seguin, working across San Antonio, Austin, the Hill Country and the
              Gulf Coast.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href="tel:+18303909588"
                className="inline-flex items-center gap-2 bg-red px-6 py-3.5 font-display text-base uppercase tracking-wider text-white shadow-bold transition hover:bg-red-deep"
              >
                <Phone className="h-5 w-5" /> +1 (830) 390-9588
              </a>
              <Link
                to="/estimate-quotes"
                className="inline-flex items-center gap-2 bg-white px-6 py-3.5 font-display text-base uppercase tracking-wider text-navy-deep shadow-bold transition hover:bg-cream"
              >
                Get a Free Estimate <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </section>

        <section className="py-12 lg:py-16">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-3 lg:px-8">
            <div className="lg:col-span-2">
              <h2 className="font-display text-2xl font-bold uppercase text-navy-deep md:text-3xl">
                Questions Texas customers ask us most
              </h2>
              <dl className="mt-6 space-y-5">
                {FAQS.map((f) => (
                  <div key={f.q} className="border-2 border-navy-deep bg-white p-5 shadow-card">
                    <dt className="font-display text-lg font-bold uppercase leading-tight text-navy-deep">
                      {f.q}
                    </dt>
                    <dd className="mt-2 text-base leading-relaxed text-navy-deep/80">{f.a}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <aside className="space-y-6">
              <div className="border-2 border-navy-deep bg-white p-5 shadow-card">
                <h2 className="font-display text-lg font-bold uppercase text-navy-deep">
                  Visit the shop
                </h2>
                <p className="mt-2 flex items-start gap-2 text-sm text-navy-deep/80">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-red" />
                  959 East Kingsbury Street
                  <br />
                  Seguin, TX 78155
                </p>
                <div className="mt-4">
                  <BusinessHours />
                </div>
              </div>

              <div className="border-2 border-navy-deep bg-white p-5 shadow-card">
                <h2 className="font-display text-lg font-bold uppercase text-navy-deep">
                  City pages
                </h2>
                <p className="mt-2 text-sm text-navy-deep/75">
                  Local projects, coverage and answers for the markets we work in most.
                </p>
                <ul className="mt-3 grid grid-cols-2 gap-2">
                  {CITY_HUBS.map((c) => (
                    <li key={c.slug}>
                      <Link
                        to={`/${c.slug}` as string}
                        className="block text-sm font-medium text-navy-deep underline decoration-red decoration-2 underline-offset-4 hover:text-red"
                      >
                        {c.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border-2 border-navy-deep bg-white p-5 shadow-card">
                <h2 className="font-display text-lg font-bold uppercase text-navy-deep">
                  Still have a question?
                </h2>
                <p className="mt-2 text-sm text-navy-deep/75">
                  Send photos and rough measurements — we'll answer the same business day.
                </p>
                <Link
                  to="/estimate-quotes"
                  className="mt-4 inline-flex items-center gap-2 bg-red px-5 py-3 font-display text-sm uppercase tracking-wider text-white transition hover:bg-red-deep"
                >
                  Request a Quote <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
